import type { ErrorResponse } from '../dto/shared/ErrorResponse'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export type AuthRole = 'CUSTOMER' | 'CRAFTSMAN' | 'ADMIN'

export interface StoredAuth {
  token: string
  role: AuthRole
}

const AUTH_STORAGE_KEY = 'solarbid.auth'

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredAuth) : null
  } catch {
    return null
  }
}

/** Persistent login (task: Handwerker-Login): kept in localStorage so a page reload doesn't log
 *  the user out, for both customer and craftsman accounts. */
let currentAuth: StoredAuth | null = readStoredAuth()

export function setAuth(token: string, role: AuthRole) {
  currentAuth = { token, role }
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(currentAuth))
}

export function clearAuth() {
  currentAuth = null
  localStorage.removeItem(AUTH_STORAGE_KEY)
}

export function getStoredAuth(): StoredAuth | null {
  return currentAuth
}

export class ApiError extends Error {
  status: number
  path?: string
  timestamp?: string
  fieldErrors?: Record<string, string>

  constructor(status: number, message: string, details?: Partial<ErrorResponse>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.path = details?.path
    this.timestamp = details?.timestamp
    this.fieldErrors = details?.fieldErrors
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(currentAuth ? { Authorization: `Bearer ${currentAuth.token}` } : {}),
        ...options?.headers,
      },
    })
  } catch {
    throw new ApiError(0, 'Backend nicht erreichbar. Läuft der Server?', { path })
  }

  if (!response.ok) {
    try {
      const body = (await response.json()) as ErrorResponse
      throw new ApiError(response.status, body.message, body)
    } catch (err) {
      if (err instanceof ApiError) throw err
      throw new ApiError(response.status, `Request fehlgeschlagen mit Status ${response.status}`, { path })
    }
  }

  // Some endpoints return 200 with an empty body (Kotlin `Unit` controller methods, e.g.
  // forgotPassword) rather than 204 - response.json() throws "Unexpected end of JSON input" on
  // an empty body, so check the actual text instead of trusting the status code.
  try {
    const text = await response.text()
    return (text ? (JSON.parse(text) as T) : (undefined as T))
  } catch {
    // The connection can drop *after* a successful (ok) status but *while* the body is still
    // streaming in (e.g. flaky mobile network) - unlike the fetch() call above, that failure
    // surfaces here, not there. Without this catch it would reach the caller as a raw, untranslated
    // browser error (e.g. "network error") instead of the same friendly message used for a fetch()
    // that never even connects.
    throw new ApiError(0, 'Backend nicht erreichbar. Läuft der Server?', { path })
  }
}

export const httpClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
}
